import { apiService } from './api';
import { AdminPermissionKey } from '../constants/adminPermissions';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'SUPER_ADMIN' | 'PUBLISHER' | 'CLIENT' | 'AGENCY';
  isActive: boolean;
  twoFactorEnabled?: boolean;
  clientId?: string;
  adminPermissions?: AdminPermissionKey[];
  /** First client linked via clients.agencyId (agency login). */
  agencyClientId?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'SUPER_ADMIN' | 'PUBLISHER' | 'CLIENT' | 'AGENCY';
  publisherId?: string;
  clientId?: string;
  adminPermissions?: AdminPermissionKey[];
}

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  role?: 'ADMIN' | 'SUPER_ADMIN' | 'PUBLISHER' | 'CLIENT' | 'AGENCY';
  isActive?: boolean;
  publisherId?: string;
  clientId?: string;
  adminPermissions?: AdminPermissionKey[];
}

export interface UsersResponse {
  success: boolean;
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface UserResponse {
  success: boolean;
  data: User;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  includeInactive?: boolean;
}

class UserService {
  async getUsers(filters: UserFilters = {}): Promise<UsersResponse> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.includeInactive) params.append('includeInactive', 'true');

    const queryString = params.toString();
    const url = `/users${queryString ? `?${queryString}` : ''}`;
    
    return await apiService.get<UsersResponse>(url);
  }

  async getUserById(id: string): Promise<UserResponse> {
    return await apiService.get<UserResponse>(`/users/${id}`);
  }

  async createUser(userData: CreateUserRequest): Promise<UserResponse> {
    return await apiService.post<UserResponse>('/users', userData);
  }

  async updateUser(id: string, userData: UpdateUserRequest): Promise<UserResponse> {
    return await apiService.put<UserResponse>(`/users/${id}`, userData);
  }

  async deleteUser(id: string): Promise<{ success: boolean; message: string; type?: string }> {
    return await apiService.delete(`/users/${id}`);
  }

  async changeUserEmail(id: string, newEmail: string): Promise<UserResponse> {
    return await apiService.put<UserResponse>(`/users/${id}/change-email`, { newEmail });
  }

  async changeUserPassword(id: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    return await apiService.put(`/users/${id}/change-password`, { newPassword });
  }

  getRoleDisplayName(role: string): string {
    switch (role) {
      case 'SUPER_ADMIN':
        return 'Super Admin';
      case 'ADMIN':
        return 'Admin';
      case 'PUBLISHER':
        return 'Publisher';
      case 'AGENCY':
        return 'Agency';
      case 'CLIENT':
        return 'Client';
      default:
        return role;
    }
  }

  formatUserName(user: User): string {
    return `${user.firstName} ${user.lastName}`;
  }
}

export const userService = new UserService();
